import { useEffect, useRef, useState } from 'react';
import { ApiClientError, apiClient, formatUnits } from '../../core/api';
import type { HistoryPage, LedgerEntry, UsageEntry } from '../../core/api';
import type { BackupLedgerEntry } from '../../core/db';
import { listBackupLedgerEntries } from '../backup/ledger';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import styles from './commerce.module.css';
import { accountMeta } from './componentMeta';

const ACCOUNT_HISTORY_META = buildComponentMeta(accountMeta('account.history'));
type History =
  | { kind: 'ledger'; rows: LedgerEntry[] }
  | { kind: 'usage'; rows: UsageEntry[] }
  | { kind: 'backup'; rows: BackupLedgerEntry[] };
const LIMIT = 50;
export function AccountHistory({
  userId,
  onSessionExpired,
}: {
  userId: string;
  onSessionExpired: () => void;
}) {
  const [kind, setKind] = useState<'ledger' | 'usage' | 'backup'>('ledger');
  const [offset, setOffset] = useState(0);
  const [history, setHistory] = useState<History | null>(null);
  const [page, setPage] = useState<HistoryPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [backupRows, setBackupRows] = useState<BackupLedgerEntry[] | null>(null);
  const sequence = useRef(0);
  const ref = useComponentMeta<HTMLDivElement>(ACCOUNT_HISTORY_META);

  useEffect(() => {
    let alive = true;
    void listBackupLedgerEntries(userId)
      .then((rows) => {
        if (alive) setBackupRows(rows);
      })
      .catch(() => {
        if (alive) setBackupRows([]);
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  useEffect(() => {
    if (kind !== 'backup') return;
    setHistory(backupRows === null ? null : { kind: 'backup', rows: backupRows });
    setPage(null);
    setError(null);
  }, [kind, backupRows]);

  useEffect(() => {
    if (kind === 'backup') return;
    const current = ++sequence.current;
    const controller = new AbortController();
    setHistory(null); setPage(null); setError(null);
    async function load() {
      try {
        if (kind === 'ledger') {
          const response = await apiClient.ledgerPage(offset, LIMIT, controller.signal);
          if (controller.signal.aborted || sequence.current !== current) return;
          setHistory({ kind: 'ledger', rows: response.entries.filter((entry) => entry.type !== 'reservation' && entry.type !== 'refund') }); setPage(response);
        } else {
          const response = await apiClient.usagePage(offset, LIMIT, controller.signal);
          if (controller.signal.aborted || sequence.current !== current) return;
          setHistory({ kind: 'usage', rows: response.usage }); setPage(response);
        }
      } catch (failure) {
        if (controller.signal.aborted || sequence.current !== current) return;
        if (failure instanceof ApiClientError && failure.status === 401) { onSessionExpired(); return; }
        setError('历史记录暂不可用，请重试');
      }
    }
    void load();
    const cleanupSequence = current + 1;
    return () => { controller.abort(); sequence.current = cleanupSequence; };
  }, [kind, offset, reload, onSessionExpired]);
  function switchTo(next: typeof kind) { setHistory(null); setPage(null); setError(null); setOffset(0); setKind(next); }
  function move(next: number) { setHistory(null); setPage(null); setError(null); setOffset(next); }
  const hasBackup = (backupRows?.length ?? 0) > 0;
  return <div ref={ref} data-ui-id={ACCOUNT_HISTORY_META.id} data-ui-role="container" className={styles.card}>
    <h2 className={styles.sectionTitle}>完整流水、用量与备份流水</h2>
    <div className={styles.row}><button type="button" className={styles.secondary} aria-pressed={kind === 'ledger'} onClick={() => switchTo('ledger')}>余额流水</button><button type="button" className={styles.secondary} aria-pressed={kind === 'usage'} onClick={() => switchTo('usage')}>模型用量</button>{hasBackup ? <button type="button" className={styles.secondary} aria-pressed={kind === 'backup'} onClick={() => switchTo('backup')}>备份流水（只读）</button> : null}</div>
    {error ? <><p role="alert" className={styles.error}>{error}</p><button type="button" className={styles.secondary} onClick={() => setReload((value) => value + 1)}>重试历史记录</button></> : history === null ? <p role="status" className={styles.hint}>{kind === 'backup' ? '正在读取本地备份流水…' : '正在加载历史记录…'}</p> : history.rows.length === 0 ? <p className={styles.empty}>{kind === 'backup' ? '本机还没有备份流水快照' : '本页暂无记录'}</p> : history.kind === 'ledger' ? history.rows.map((entry) => <div key={entry.id} className={styles.ledgerItem}><div className={styles.row}><span>{entry.description || entry.type}</span><strong>{entry.amountUnits > 0 ? '+' : ''}{formatUnits(entry.amountUnits)}</strong></div><p className={styles.muted}>余额 {formatUnits(entry.balanceAfterUnits)} 栀籽 · {new Date(entry.createdAt).toLocaleString()}</p></div>) : history.kind === 'usage' ? history.rows.map((entry) => <div key={entry.id} className={styles.ledgerItem}><div className={styles.row}><span>模型请求</span><strong>扣费 {formatUnits(entry.chargedUnits)} 栀籽</strong></div><p className={styles.muted}>输入 {entry.inputTokens} · 输出 {entry.outputTokens} · 缓存输入 {entry.cachedInputTokens}</p><p className={styles.muted}>{new Date(entry.createdAt).toLocaleString()}</p></div>) : history.rows.map((row) => <div key={row.id} className={styles.ledgerItem}><div className={styles.row}><span>{row.entry.description || row.entry.type}</span><strong>{row.entry.amountUnits > 0 ? '+' : ''}{formatUnits(row.entry.amountUnits)}</strong></div><p className={styles.muted}>余额 {formatUnits(row.entry.balanceAfterUnits)} 栀籽 · 备份于 {new Date(row.capturedAt).toLocaleString()}</p></div>)}
    {kind === 'backup' ? null : <div className={styles.row}><button type="button" className={styles.secondary} disabled={!page || offset === 0} onClick={() => move(Math.max(0, offset - LIMIT))}>上一页历史</button><span className={styles.hint}>第 {Math.floor(offset / LIMIT) + 1} 页</span><button type="button" className={styles.secondary} disabled={!page?.hasMore} onClick={() => move(offset + LIMIT)}>下一页历史</button></div>}
  </div>;
}
